import { IdTokenClaims } from 'oidc-client-ts';
import { isPresalesAiOrganization } from '../constants/aiRecommendationAccess';
import { canAccessOnboardingResetFromSubdomain } from '../constants/onboardingResetAccess';

export interface UserProfile extends IdTokenClaims {
  org_admin: boolean;
  org_id: string;
  locale: string;
  org_locale?: string;
  [claim: string]: unknown;
}

// API Response types for /user/profile endpoint
export interface UserRole {
  roleId: number;
  name: string;
  displayName: string;
  hierarchyLevel: number;
}

export interface UserMembership {
  status: string;
  department: string | null;
  title: string | null;
}

export interface UserOrganization {
  id: string;
  name: string;
  status: string;
  accountType: string;
  appAccountType: 'legacy' | 'compliance_education' | 'trial';
  subdomain?: string | null;
}

export interface UserProfileData {
  userId: string;
  orgId: string;
  email: string;
  firstName: string;
  lastName: string;
  displayName: string | null;
  locale: string;
  timezone: string;
  userType: string | null;
  organization?: UserOrganization; // Optional for safety - backend should always provide it
  membership: UserMembership;
  roles: UserRole[];
}

export interface UserProfileResponse {
  success: boolean;
  data: UserProfileData;
}

// User Type constants - for access control based on API userType property
export const USER_TYPES = {
  SUPER_ADMIN: 'super_admin',
  STANDARD: 'standard_user',
  ACCOUNT_ADMIN: 'account_admin',
  CONTENT_MANAGER: 'content_manager',
} as const;

export type UserType = (typeof USER_TYPES)[keyof typeof USER_TYPES];

// Role ID constants - for access control based on roles.roleId property
export const ROLE_IDS = {
  STANDARD_USER: 4, // Standard user role ID from database
} as const;

// Type guard to check if a value is a valid user type
export function isValidUserType(type: unknown): type is UserType {
  return typeof type === 'string' && Object.values(USER_TYPES).includes(type as UserType);
}

// Helper to get user type with validation and fallback
export function getUserType(profileData: UserProfileData | null): UserType {
  const userType = profileData?.userType;

  // First check if userType is directly available and valid
  if (isValidUserType(userType)) {
    return userType;
  }

  // Special case: For standard users, userType may be null/undefined
  // In this case, check roles array for 'standard_user'
  if (!userType && profileData?.roles && profileData.roles.length > 0) {
    const roleName = profileData.roles[0].name;

    if (roleName === USER_ROLES.STANDARD_USER) {
      return USER_TYPES.STANDARD;
    }
  }

  // Log unexpected values for monitoring
  if (userType && !isValidUserType(userType)) {
    console.warn(`[UserProfile] Unexpected userType value: ${userType}. Defaulting to standard.`);
  }

  // Default to standard user if not specified or invalid
  return USER_TYPES.STANDARD;
}

// Role constants - single source of truth for all role names
export const USER_ROLES = {
  STANDARD_USER: 'standard_user',
  SUPER_ADMIN: 'super_admin',
  ACCOUNT_ADMIN: 'account_admin',
  CONTENT_MANAGER: 'content_manager',
} as const;

// Type-safe role name type
export type UserRoleName = (typeof USER_ROLES)[keyof typeof USER_ROLES];

// Role display names mapping - for UI presentation
export const USER_ROLE_DISPLAY_NAMES: Record<UserRoleName, string> = {
  [USER_ROLES.STANDARD_USER]: 'Standard User',
  [USER_ROLES.SUPER_ADMIN]: 'Super Administrator',
  [USER_ROLES.ACCOUNT_ADMIN]: 'Account Administrator',
  [USER_ROLES.CONTENT_MANAGER]: 'Content Manager',
};

// Type guard to check if a value is a valid role name
export function isValidRoleName(role: string): role is UserRoleName {
  return Object.values(USER_ROLES).includes(role as UserRoleName);
}

// Helper to get user's primary role with type safety
export function getUserPrimaryRole(profileData: UserProfileData | null): UserRoleName | null {
  if (!profileData || !profileData.roles || profileData.roles.length === 0) {
    return null;
  }
  const roleName = profileData.roles[0].name;
  return isValidRoleName(roleName) ? roleName : null;
}

// Helper to check if user has any elevated permissions (not standard_user)
export function hasElevatedPermissions(profileData: UserProfileData | null): boolean {
  const role = getUserPrimaryRole(profileData);
  return role !== null && role !== USER_ROLES.STANDARD_USER;
}

// Helper to check if user is super admin (can update account types)
export function isSuperAdmin(profileData: UserProfileData | null): boolean {
  // Check userType field from database (not roles array)
  // userType comes from users.user_type column
  return profileData?.userType?.toLowerCase() === USER_ROLES.SUPER_ADMIN;
}

// Helper to get formatted display name for a role
export function getRoleDisplayName(roleName: string | null | undefined): string {
  if (!roleName) return '';

  // Check if it's a known role and return the display name
  if (isValidRoleName(roleName)) {
    return USER_ROLE_DISPLAY_NAMES[roleName];
  }

  // Fallback: Convert snake_case to Title Case
  return roleName
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

// ============================================================================
// ACCESS CONTROL - Route and Feature Access Based on User Type
// ============================================================================

/**
 * Helper function to get environment configuration from global window object
 * Centralizes access to AppConfig values stored on window for access control
 *
 * @returns Environment configuration with subdomain and local flag
 */
function getEnvironmentConfig(): { appSubdomain: string | undefined; isLocal: boolean } {
  return {
    appSubdomain: window.__APP_SUBDOMAIN__,
    isLocal: window.__IS_LOCAL__ ?? false,
  };
}

/**
 * Get the leftmost (first) subdomain from the current hostname.
 * Used for compliance-ops check: compliance-ops.compliance-education.diligentoneplatform-dev.com
 * has first subdomain "compliance-ops". getPlatformUrlParts may return "compliance-education"
 * as appSubdomain, so we derive the compliance-ops segment from the hostname directly.
 */
function getFirstSubdomainFromHostname(): string | undefined {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  if (!hostname || hostname === 'localhost') return undefined;
  const firstSegment = hostname.split('.')[0];
  return firstSegment || undefined;
}

/**
 * Check if the current environment matches Compliance Operations requirements
 * This checks that:
 * - First subdomain in URL hostname is 'compliance-ops', 'compliance-eu-ops', or 'compliance-uk-ops' (skipped in local development)
 * - Organization name starts with 'Compliance Operations' to support all regions (always checked)
 *
 * Uses hostname first segment (e.g. compliance-ops from compliance-ops.compliance-education....)
 * because getPlatformUrlParts may return a different segment (e.g. compliance-education).
 */
function isComplianceOpsEnvironment(profileData: UserProfileData | null): boolean {
  const { isLocal } = getEnvironmentConfig();
  const firstSubdomain = getFirstSubdomainFromHostname();

  // Check subdomain (case-insensitive) using first hostname segment
  // In local development, skip subdomain check (localhost has no subdomain)
  // Matches US (compliance-ops), EU (compliance-eu-ops), and UK DR (compliance-uk-ops) ops orgs
  const isComplianceOpsSubdomain = isLocal ? true :
    firstSubdomain?.toLowerCase() === 'compliance-ops' ||
    firstSubdomain?.toLowerCase() === 'compliance-eu-ops' ||
    firstSubdomain?.toLowerCase() === 'compliance-uk-ops';

  // Check organization name (case-insensitive)
  // This check is ALWAYS enforced, even in local development
  // Supports both US and EU variants: 'Compliance Operations', 'Compliance Operations EU', etc.
  const orgName = profileData?.organization?.name?.toLowerCase() || '';
  const isComplianceOpsOrg = orgName.startsWith('compliance operations');

  const isValid = isComplianceOpsSubdomain && isComplianceOpsOrg;

  // Log access check details for debugging
  console.log('[Access Control] Compliance Operations environment check', {
    currentUrl: window.location.href,
    isLocal,
    firstSubdomain: isLocal ? 'localhost (check skipped)' : firstSubdomain,
    expectedSubdomain: 'compliance-ops, compliance-eu-ops, or compliance-uk-ops',
    isComplianceOpsSubdomain,
    organizationName: profileData?.organization?.name,
    expectedOrgName: 'Compliance Operations (any region)',
    isComplianceOpsOrg,
    result: isValid ? 'PASS' : 'FAIL',
  });

  return isValid;
}

/**
 * Check if user can access the dashboard page
 * Access: super_admin, account_admin
 * Additional requirement: Must be in Compliance Operations environment
 */
export function canAccessDashboard(profileData: UserProfileData | null): boolean {
  const userType = getUserType(profileData);
  const hasRequiredRole = userType === USER_TYPES.SUPER_ADMIN || userType === USER_TYPES.ACCOUNT_ADMIN;

  // Dashboard requires Compliance Operations environment check
  return hasRequiredRole && isComplianceOpsEnvironment(profileData);
}

/**
 * Check if user can access the accounts page
 * Access: super_admin, account_admin
 * Additional requirement: Must be in Compliance Operations environment
 */
export function canAccessAccounts(profileData: UserProfileData | null): boolean {
  const userType = getUserType(profileData);
  const hasRequiredRole = userType === USER_TYPES.SUPER_ADMIN || userType === USER_TYPES.ACCOUNT_ADMIN;

  // Accounts page requires Compliance Operations environment check
  return hasRequiredRole && isComplianceOpsEnvironment(profileData);
}

/**
 * Check if user can access the users management page
 * Access: super_admin only
 * Additional requirement: Must be in Compliance Operations environment
 */
export function canAccessUsers(profileData: UserProfileData | null): boolean {
  const userType = getUserType(profileData);
  const hasRequiredRole = userType === USER_TYPES.SUPER_ADMIN;

  // Users page requires Compliance Operations environment check
  return hasRequiredRole && isComplianceOpsEnvironment(profileData);
}

/**
 * Check if user can access the content authoring templates page (/templates)
 * Access: super_admin only (Phase 1 is Spark-team internal)
 * Additional requirements:
 * - Content Authoring feature flag enabled (every recognised tier, including production)
 * - Must be in Compliance Operations environment
 */
export function canAccessTemplates(
  profileData: UserProfileData | null,
  contentAuthoringEnabled: boolean,
): boolean {
  if (!contentAuthoringEnabled) {
    return false;
  }

  const userType = getUserType(profileData);
  const hasRequiredRole = userType === USER_TYPES.SUPER_ADMIN;

  // Templates page requires Compliance Operations environment check
  return hasRequiredRole && isComplianceOpsEnvironment(profileData);
}

/**
 * Check if user can access content management pages (/manage-content/*)
 * Access: super_admin, content_manager
 * Additional requirement: Must be in Compliance Operations environment
 */
export function canAccessContentManagement(profileData: UserProfileData | null): boolean {
  const userType = getUserType(profileData);
  const hasRequiredRole = userType === USER_TYPES.SUPER_ADMIN || userType === USER_TYPES.CONTENT_MANAGER;

  // Content management requires Compliance Operations environment check
  return hasRequiredRole && isComplianceOpsEnvironment(profileData);
}

/**
 * Check if user can review submitted content (/review-center/review-content/{id})
 * Access: super_admin, account_admin
 * Additional requirement: Must be in Compliance Operations environment
 */
export function canReviewContent(profileData: UserProfileData | null): boolean {
  const userType = getUserType(profileData);
  const hasRequiredRole = userType === USER_TYPES.SUPER_ADMIN || userType === USER_TYPES.ACCOUNT_ADMIN;

  // Review content requires Compliance Operations environment check
  return hasRequiredRole && isComplianceOpsEnvironment(profileData);
}

/**
 * Check if user can access courses page
 * Access: Users with standard user role (roleId = 4) who are NOT in Compliance Operations environment
 *
 * Requirements:
 * - First subdomain is NOT 'compliance-ops' (skipped in local development)
 * - Organization name is NOT 'Compliance Operations' (always checked)
 * - User has standard user role (roleId = 4)
 */
export function canAccessCourses(profileData: UserProfileData | null): boolean {
  const { isLocal } = getEnvironmentConfig();
  const firstSubdomain = getFirstSubdomainFromHostname();

  // Check if user has standard user role
  const userType = getUserType(profileData);
  const hasStandardUserRole = !userType || userType === USER_TYPES.STANDARD;

  // Check first subdomain is NOT a compliance ops subdomain (case-insensitive)
  // In local development, skip subdomain check (localhost has no subdomain)
  // Blocks US (compliance-ops), EU (compliance-eu-ops), and UK DR (compliance-uk-ops) ops orgs
  const isNotComplianceOpsSubdomain = isLocal ? true :
    firstSubdomain?.toLowerCase() !== 'compliance-ops' &&
    firstSubdomain?.toLowerCase() !== 'compliance-eu-ops' &&
    firstSubdomain?.toLowerCase() !== 'compliance-uk-ops';

  // Check organization name is NOT Compliance Operations (case-insensitive)
  // This check is ALWAYS enforced, even in local development
  // Blocks all variants: 'Compliance Operations', 'Compliance Operations EU', etc.
  const orgName2 = profileData?.organization?.name?.toLowerCase() || '';
  const isNotComplianceOpsOrg = !orgName2.startsWith('compliance operations');

  // Presales orgs (e.g. sales-ops) use AI recommendation, not customer courses
  const isNotPresalesAiOrg = !isPresalesAiOrganization(
    profileData?.organization?.name,
    isLocal ? undefined : firstSubdomain,
  );

  const canAccess = hasStandardUserRole && isNotComplianceOpsSubdomain && isNotComplianceOpsOrg && isNotPresalesAiOrg;

  // Log access check details for debugging
  console.log('[Access Control] Courses access check', {
    currentUrl: window.location.href,
    isLocal,
    firstSubdomain: isLocal ? 'localhost (check skipped)' : firstSubdomain,
    expectedCondition: 'NOT compliance-ops',
    isNotComplianceOpsSubdomain,
    organizationName: profileData?.organization?.name,
    expectedOrgCondition: 'NOT Compliance Operations',
    isNotComplianceOpsOrg,
    isNotPresalesAiOrg,
    hasStandardUserRole,
    expectedRoleId: ROLE_IDS.STANDARD_USER,
    result: canAccess ? 'PASS' : 'FAIL',
  });

  return canAccess;
}

/**
 * Check if user can access the standard-user settings page (/settings).
 * Eligibility matches {@link canAccessCourses} today; split for clearer route and nav guards.
 */
export function canAccessSettings(profileData: UserProfileData | null): boolean {
  return canAccessCourses(profileData);
}

/**
 * Admin path: internal dev tools + Compliance Operations + super_admin or account_admin.
 */
export function canAccessAiRecommendationAsAdmin(
  profileData: UserProfileData | null,
  internalDevToolsEnabled: boolean,
): boolean {
  if (!internalDevToolsEnabled) {
    return false;
  }

  const userType = getUserType(profileData);
  const hasRequiredRole =
    userType === USER_TYPES.SUPER_ADMIN || userType === USER_TYPES.ACCOUNT_ADMIN;

  return hasRequiredRole && isComplianceOpsEnvironment(profileData);
}

/**
 * Presales path: sales-ops org (name + subdomain) + standard_user only.
 */
export function canAccessAiRecommendationAsSalesOps(
  profileData: UserProfileData | null,
  salesOpsAiRecommendationEnabled: boolean,
): boolean {
  if (!salesOpsAiRecommendationEnabled) {
    return false;
  }

  const userType = getUserType(profileData);
  if (userType !== USER_TYPES.STANDARD) {
    return false;
  }

  if (getUserPrimaryRole(profileData) !== USER_ROLES.STANDARD_USER) {
    return false;
  }

  const { isLocal } = getEnvironmentConfig();
  const firstSubdomain = isLocal ? undefined : getFirstSubdomainFromHostname();

  return isPresalesAiOrganization(profileData?.organization?.name, firstSubdomain);
}

/**
 * AI Recommendation page and API usage.
 * Admins: unchanged dev/compliance-ops gate. Presales: allowlisted orgs only.
 */
export function canAccessAiRecommendation(
  profileData: UserProfileData | null,
  options: {
    internalDevToolsEnabled: boolean;
    salesOpsAiRecommendationEnabled: boolean;
  },
): boolean {
  return (
    canAccessAiRecommendationAsAdmin(profileData, options.internalDevToolsEnabled) ||
    canAccessAiRecommendationAsSalesOps(profileData, options.salesOpsAiRecommendationEnabled)
  );
}

/**
 * Training plan email deep link + secure attachment gateway (CEE-929).
 * Matches recommendation-advisor `canAccessTrainingPlanGatewayApi`.
 */
export function isSparkComplianceOpsSuperAdmin(profileData: UserProfileData | null): boolean {
  const userType = getUserType(profileData);
  const isSuperAdmin =
    userType === USER_TYPES.SUPER_ADMIN ||
    getUserPrimaryRole(profileData) === USER_ROLES.SUPER_ADMIN;

  return isSuperAdmin && isComplianceOpsEnvironment(profileData);
}

/** Settings onboarding reset — explicit demo allowlist only. */
export function canAccessOnboardingReset(profileData: UserProfileData | null): boolean {
  if (!profileData) {
    return false;
  }

  const { isLocal } = getEnvironmentConfig();

  return canAccessOnboardingResetFromSubdomain(profileData.organization?.subdomain, isLocal);
}

export function canAccessTrainingPlanGateway(
  profileData: UserProfileData | null,
  options: {
    internalDevToolsEnabled: boolean;
    salesOpsAiRecommendationEnabled: boolean;
  },
): boolean {
  if (isSparkComplianceOpsSuperAdmin(profileData)) {
    return true;
  }

  return canAccessAiRecommendation(profileData, options);
}
